<template>
  <header class="header">
    <div class="container">
      <div class="header__wrapper">
        <router-link :to="translations.i18nRoute({ name: 'home' })">
          <img src="@images/logo.png" alt="logo" class="header__logo">
        </router-link>
        <nav class="header__nav">
          <router-link
              v-for="category of categories"
              :key="category.node.uuid"
              :to="translations.i18nRoute({ name: 'store', params: { category: category.node.slug } })"
              class="header__nav-item"
          >
            {{ category.node.name }}
          </router-link>
        </nav>
        <div class="header__actions" v-if="user">
          <router-link :to="translations.i18nRoute({ name: 'profile-topup' })" class="header__actions-balance">
            <img src="@icons/currency.svg" alt="currency">
            <p>{{ userBalance }}</p>
          </router-link>
          <div class="header__actions-inner">
            <el-badge :value="productsInWishlistQuantity">
              <router-link :to="translations.i18nRoute({ name: 'wishlist' })">
                <img src="@icons/wishlistIcon.svg" alt="wishlist">
              </router-link>
            </el-badge>
            <el-badge :value="productsInCartQuantity">
              <router-link :to="translations.i18nRoute({ name: 'cart' })">
                <img src="@icons/cartIcon.svg" alt="cart">
              </router-link>
            </el-badge>
            <router-link :to="translations.i18nRoute({ name: 'profile' })">
              <img src="@icons/accountIcon.svg" alt="account">
            </router-link>
          </div>
        </div>
        <div class="header__auth" v-else>
          <router-link :to="translations.i18nRoute({ name: 'login' })" class="header__auth-login">{{ t('buttons.login') }}</router-link>
          <router-link :to="translations.i18nRoute({ name: 'register' })" class="header__auth-reg">
            {{ t('buttons.createAcc') }}
            <img src="@icons/registerArrow.svg" alt="register">
          </router-link>
        </div>
        <svg class="header__menu" @click="isMenuActive = !isMenuActive" width="40px" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" fill="none">
          <path fill="currentColor" fill-rule="evenodd"
              d="M18 5a1 1 0 100-2H2a1 1 0 000 2h16zm0 4a1 1 0 100-2h-8a1 1 0 100 2h8zm1 3a1 1 0 01-1 1H2a1 1 0 110-2h16a1 1 0 011 1zm-1 5a1 1 0 100-2h-8a1 1 0 100 2h8z"/>
        </svg>
      </div>
    </div>
    <base-menu :categories="categories" :isActive="isMenuActive" @close-modal="handleCloseModal" />
  </header>
</template>

<script setup>
import translations from "@/core/helpers/translations.js";
import {computed, onMounted, ref, watch} from "vue";
import {useAuthStore} from "@/stores/auth.js";
import {useI18n} from "vue-i18n";
import {useCartStore} from "@/stores/cart.js";
import {useWishlistStore} from "@/stores/wishlist.js";
import {Categories} from "@/core/api/categories.js";
import BaseMenu from "@/components/base/base-menu.vue";

const {t, locale} = useI18n()
const authStore = useAuthStore()
const cartStore = useCartStore()
const wishlistStore = useWishlistStore()

const user = computed(() => {
  return authStore.user
})
const userBalance = computed(() => {
  return authStore.user ? authStore.user.balance.amount : 0
})
const productsInCartQuantity = computed(() => {
  return cartStore.currentOrder.orderProducts ? cartStore.currentOrder.orderProducts.edges.length : 0
})
const productsInWishlistQuantity = computed(() => {
  return wishlistStore.wishlist.products ? wishlistStore.wishlist.products.edges.length : 0
})

const isMenuActive = ref(false)
const handleCloseModal = () => {
  isMenuActive.value = false;
}

const categories = ref([])

const getCategories = async () => {
  const res = await Categories.getCategories()
  categories.value = res.data.categories.edges
}

watch(locale, async () => {
  await getCategories()
})

onMounted(async () => {
  await getCategories()
})
</script>

<style lang="scss" scoped>
.header {
  padding-top: 25px;
  background-color: transparent;
  position: sticky;
  top: 0;
  z-index: 3;
  width: 100%;

  &__wrapper {
    position: relative;
    padding: 16px 55px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: 11px;
    background-color: $white;
    box-shadow: 0 4px 11.6px 0 rgba(0, 0, 0, 0.20);
    backdrop-filter: blur(4px);
  }

  &__logo {
    width: 115px;
  }

  &__nav {
    top: 50%;
    position: absolute;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;
    gap: 30px;

    &-item {
      transition: 0.2s;
      color: #2B3044;
      font-size: 16px;
      font-weight: 500;

      &:hover {
        color: #1e2336;
      }
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 50px;

    &-balance {
      display: flex;
      align-items: center;
      gap: 5px;

      & img {
        width: 30px;
      }

      & p {
        color: #2B3044;
        font-weight: 500;
      }
    }

    &-inner {
      display: flex;
      align-items: center;
      gap: 35px;

      & a {
        position: relative;
      }
    }
  }

  &__auth {
    width: fit-content;
    display: flex;
    align-items: center;
    gap: 25px;

    color: $white;
    font-size: 16px;
    font-weight: 500;

    &-login {
      cursor: pointer;
      border-radius: 12px 30px 30px 12px;
      background-color: #5B194F;
      padding: 9px 30px;
      transition: 0.2s;

      &:hover {
        background-color: #722363;
      }
    }

    &-reg {
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 9px 20px;
      border-radius: 12px 30px 30px 12px;
      border: 0.5px solid #5B194F;
      background-color: $white;
      box-shadow: 0 0 11.6px 0 rgba(0, 0, 0, 0.20);

      color: #2B3044;
    }
  }

  &__menu {
    cursor: pointer;
    display: none;
  }
}

:deep(.el-badge__content) {
  background-color: #9f5d92;
}

@media (max-width: 1000px) {
  .header {
    &__menu {
      display: block;
    }

    &__nav {
      display: none;
    }

    &__actions {
      display: none;
    }

    &__auth {
      display: none;
    }
  }
}
</style>